import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IProperty } from '@shared/models/properties';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';

import { MAP_US_CENTER } from '@core/constants/map';
import { MapService } from '@core/services/map.service';
import { MapMarkerService } from '@core/services/map-marker.service';
import {
  ACCOUNT_SETUP_MIN_DATE,
  REVIEW_CBSA_PREFERENCES_MIN_DATE,
} from '@core/constants/auth';
import { AuthModal } from '@shared/models/auth';

import { Store } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { setAuthModal } from '@state/auth/auth.actions';
import { selectAuth, selectAuthModal } from '@state/auth/auth.selectors';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-home-map-view',
  templateUrl: './map-view.component.html',
  styleUrls: ['./map-view.component.scss'],
})
export class MapViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) loading = false;
  @Input({ required: true }) properties?: IProperty[];

  protected isLoggedIn = false;
  protected isConfirmed = false;
  protected showSetup = false;
  protected showAuth = false;
  protected isMobile = false;
  protected authModal?: AuthModal;

  protected markers: google.maps.marker.AdvancedMarkerElement[] = [];
  protected mapRef?: google.maps.Map;
  protected firstInit = true;

  protected showAuthYellowBG = false;

  constructor(
    private deviceService: DeviceDetectorService,
    private mapService: MapService,
    private mapMarkerService: MapMarkerService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();

    this.store
      .select(selectAuth)
      .pipe(untilDestroyed(this))
      .subscribe((auth) => {
        this.isLoggedIn = !!auth.token;
        this.isConfirmed =
          !!auth.user?.emailConfirmedAt && !!auth.user?.phoneNumberConfirmedAt;
        this.showSetup =
          ((!auth.user?.preference || !auth.user.preference.roles) &&
            new Date(auth?.user?.insertedAt || '') >= ACCOUNT_SETUP_MIN_DATE) ||
          !auth.user?.cbsaPreferences ||
          auth.user?.cbsaPreferences.length === 0 ||
          new Date(auth?.user?.cbsaPreferences[0]?.updatedAt || '') <
            REVIEW_CBSA_PREFERENCES_MIN_DATE;
      });

    /**
     * Set auth modal can be called from the new sidebar component
     * in the home screen.
     */
    this.store
      .select(selectAuthModal)
      .pipe(untilDestroyed(this))
      .subscribe((authModal) => {
        this.authModal = authModal;
        if (this.showAuth) return;
        if (authModal && authModal === 'signin') {
          this.showAuth = true;
          this.showAuthYellowBG = true;
          setTimeout(() => {
            this.showAuthYellowBG = false;
          }, 300);
        }
      });
  }

  ngOnDestroy(): void {
    this.showAuth = false;
    this.store.dispatch(setAuthModal({ authModal: undefined }));
  }

  /**
   * Check if it's necessary to recenter the map based on
   * - number of properties
   * - first and last property id
   * - first and last property latitude
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['properties']) {
      if (!this.mapRef) {
        setTimeout(() => {
          this.createMap();
          this.autoCenterZoom();
        }, 0);
      } else {
        const previousProps = changes['properties'].previousValue;
        const currentProps = changes['properties'].currentValue;

        const isFirstOrLastPropertyDifferent = () => {
          const firstPrevId = previousProps[0]?.id;
          const lastPrevId = previousProps[previousProps.length - 1]?.id;
          const firstCurrId = currentProps[0]?.id;
          const lastCurrId = currentProps[currentProps.length - 1]?.id;
          return firstPrevId !== firstCurrId || lastPrevId !== lastCurrId;
        };

        const isFirstOrLastPropertyLatitudeDifferent = () => {
          const firstPrevLat = previousProps[0]?.latitude;
          const lastPrevLat = previousProps[previousProps.length - 1]?.latitude;
          const firstCurrLat = currentProps[0]?.latitude;
          const lastCurrLat = currentProps[currentProps.length - 1]?.latitude;
          return firstPrevLat !== firstCurrLat || lastPrevLat !== lastCurrLat;
        };

        if (
          previousProps &&
          currentProps &&
          previousProps.length !== currentProps.length
        ) {
          this.autoCenterZoom();
        } else if (isFirstOrLastPropertyDifferent()) {
          this.autoCenterZoom();
        } else if (isFirstOrLastPropertyLatitudeDifferent()) {
          this.autoCenterZoom();
        }
      }
    }
  }

  // Only create map if it doesn't exist
  createMap() {
    if (this.mapRef) return;
    this.mapRef = this.mapService.createMap('home-map', undefined);
  }

  /**
   * Center the map based on:
   * - authenticated status
   * - number of properties
   *
   * This method is also responsible
   * for setting the markers
   */
  autoCenterZoom() {
    if (!this.mapRef) return;
    const bounds = new google.maps.LatLngBounds();

    if (
      this.isLoggedIn &&
      this.isConfirmed &&
      this.properties &&
      this.properties.length > 0
    ) {
      for (const property of this.properties) {
        if (
          property.latitude &&
          property.latitude !== '0' &&
          property.longitude &&
          property.longitude !== '0'
        ) {
          bounds.extend(
            new google.maps.LatLng(
              parseFloat(property.latitude),
              parseFloat(property.longitude)
            )
          );
        }
      }
      if (this.properties.length === 1) {
        this.mapRef.setCenter(bounds.getCenter());
        this.mapRef.setZoom(15);
      } else {
        setTimeout(() => {
          this.mapRef?.fitBounds(bounds, this.isMobile ? 45 : 90);
        }, 1500);
        if (this.firstInit) this.firstInit = false;
      }
      this.mapRef.setValues({ minZoom: this.isMobile ? 3 : 3 });
    } else {
      this.mapRef.setCenter(MAP_US_CENTER);
      this.mapRef.setValues({ zoom: 5 });
    }

    this.setMarkers();
  }

  // Always clear markers before setting them
  setMarkers() {
    this.markers.forEach((marker) => {
      this.mapService.removeAdvancedMarker(marker);
    });

    if (this.properties && this.mapRef) {
      this.markers = [];
      google.maps.event.clearListeners(this.mapRef, 'center_changed');
      google.maps.event.clearListeners(this.mapRef, 'click');
      this.properties.forEach((property: IProperty) => {
        const newMarker = this.mapService.createMarker(this.mapRef!, property);
        if (newMarker) this.markers.push(newMarker);
      });
    }
  }

  showAuthModal(authModal: 'signin' | 'signup') {
    this.showAuth = true;
    this.store.dispatch(setAuthModal({ authModal }));
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler() {
    this.mapMarkerService.closeAllPreviews();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.tagName === 'DIV' && !target.classList.length) {
      this.mapMarkerService.closeAllPreviews();
    }
  }
}
